import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import './Checkbox';

@customElement('media-file-card')
export class MediaFileCard extends LitElement {
  static readonly styles = css`
    :host {
      --background-color: #fff;
      --border-color: #ccc;
      --border-width: 1px;
      --border-radius: 12px;
      --primary-color: #ff9800;
      font-family: Roboto, sans-serif;
      position: relative;
    }

    .card {
      box-sizing: border-box;
      overflow: hidden;
      height: 100%;
      background: var(--background-color);
      border-radius: var(--border-radius);
      border-style: solid;
      border-width: var(--border-width);
      border-color: var(--border-color);
      cursor: pointer;
      transition: all 0.2s ease-in-out;
      position: relative;
    }

    .card--disabled {
      cursor: not-allowed;
      opacity: 0.75;
      filter: grayscale(1);
    }

    .card:not(.card--disabled):hover {
      box-shadow: rgb(3 3 5 / 58%) 0px 20px 20px -20px;
      border-color: var(--primary-color);
      z-index: 1;
    }

    .card:active {
      transform: scale(0.98);
    }

    .card .preview {
      border-bottom: 1px solid var(--border-color);
      align-items: center;
      justify-content: center;
      flex-direction: row;
      display: flex;
    }

    .card .preview .selection {
      position: absolute;
      top: 12px;
      left: 12px;
      box-sizing: border-box;
      padding: 4px;
    }

    .card .preview .actions {
      position: absolute;
      top: 12px;
      right: 12px;
      box-sizing: border-box;
      padding: 4px;
    }

    .card .preview .message {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 160px;
      width: 100%;
      background: var(--border-color);
    }

    .card .preview .image {
      display: flex;
      justify-content: center;
      height: 160px;
      width: 100%;
      background: repeating-conic-gradient(
          var(--border-color) 0%,
          var(--border-color) 25%,
          transparent 0%,
          transparent 50%
        )
        50% center / 20px 20px;
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
    }

    .card .review .image img {
      margin: 0px;
      padding: 0px;
      max-height: 100%;
      max-width: 100%;
      object-fit: contain;
    }

    .card .details {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      padding: 12px;
      box-sizing: border-box;
    }

    .card .details .info {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
    }

    .card .details .info p {
      margin-block-start: 0;
      margin-block-end: 0;
    }

    .card .details .info .title {
      font-weight: bold;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 100%;
      max-width: 200px;
    }

    .card .details .info .subtitle {
    }

    .card .details .file-mime {
      box-sizing: border-box;
      border: 1px solid var(--border-color);
      background: color-mix(in srgb, var(--border-color) 25%, transparent);
      border-radius: 8px;
      padding: 2px 4px;
    }
  `;

  /**
   * Name of the file
   */
  @property({ type: String }) fileName: string = '';

  /**
   * Size of the file in bytes
   */
  @property({ type: Number }) fileSize: number = 0;

  /**
   * Type of the file
   */
  @property({ type: String }) fileType: string = '';

  /**
   * URL of the file
   */
  @property({ type: String }) url: string = '';

  /**
   * URL of the file
   */
  @property({ type: String }) previewUrl: string = '';

  /**
   * Width of the image
   */
  @property({ type: String }) width: string = '';

  /**
   * Height of the image
   */
  @property({ type: String }) height: string = '';

  /**
   * Whether the file is selectable
   */
  @property({ type: Boolean }) selectable: boolean = false;

  /**
   * Whether the file is editable
   */
  @property({ type: Boolean }) editable: boolean = false;

  /**
   * Whether the file is removable
   */
  @property({ type: Boolean }) removable: boolean = false;

  @property({ type: Boolean })
  selected = false;

  @property({ type: Boolean })
  canSelect = true;

  private readonly handleCheckboxChange = (event: CustomEvent) => {
    event.stopPropagation();
    const selected = event.detail.checked;

    const eventName = selected ? 'selected' : 'unselected';

    this.dispatchEvent(
      new CustomEvent(eventName, {
        detail: {
          selected,
        },
      })
    );
  };

  private readonly _handleRemoveChange = (event: CustomEvent) => {
    event.stopPropagation();

    this.dispatchEvent(new CustomEvent('remove'));
  };

  private readonly _handleEditChange = (event: CustomEvent) => {
    event.stopPropagation();

    this.dispatchEvent(new CustomEvent('edit'));
  };

  _renderFileSize() {
    const kb = this.fileSize; // fileSize is already in KB
    const mb = kb / 1024;
    const gb = mb / 1024;

    if (gb >= 1) {
      return html` <span>${gb.toFixed(2)} GB</span> `;
    }

    if (mb >= 1) {
      return html` <span>${mb.toFixed(2)} MB</span> `;
    }

    return html` <span>${kb.toFixed(2)} KB</span> `;
  }

  renderTitle() {
    if (this.fileType.includes('image')) {
      return html`
        <b class="title" title="${this.fileName}">${this.fileName}</b>
        <small class="subtitle">
          ${this._renderFileSize()} - ${this.width}x${this.height}
        </small>
      `;
    }

    return html`
      <b class="title" title="${this.fileName}">${this.fileName}</b>
      <span class="subtitle">${this._renderFileSize()}</span>
    `;
  }

  renderPreview() {
    if (this.fileType.includes('image')) {
      return html`
        <div class="image">
          <img src="${this.previewUrl}" alt="${this.fileName}" />
        </div>
      `;
    }

    return html`
      <div class="message">
        <p>File preview not available</p>
      </div>
    `;
  }

  renderSelectionButton() {
    if (!this.selectable) {
      return html``;
    }

    return html`
      <div class="selection">
        <media-checkbox
          .selected="${this.selected}"
          .disabled="${!this.canSelect}"
          @checked="${this.handleCheckboxChange}"
        ></media-checkbox>
      </div>
    `;
  }

  renderActionsButton() {
    if (!this.removable) {
      return html``;
    }

    return html`
      <div class="actions">
        ${this.editable
          ? html`<button @click="${this._handleEditChange}">Edit</button>`
          : html``}
        ${this.removable
          ? html`<button @click="${this._handleRemoveChange}">Remove</button>`
          : html``}
      </div>
    `;
  }

  render() {
    return html`
      <article
        class=${classMap({
          card: true,
          'card--selectable': this.selectable,
          'card--editable': this.editable,
          'card--removable': this.removable,
        })}
      >
        <div class="preview">
          ${this.renderSelectionButton()} ${this.renderActionsButton()}
          ${this.renderPreview()}
        </div>
        <div class="details">
          <div class="info">${this.renderTitle()}</div>
          <div class="file-mime">
            <small>${this.fileType}</small>
          </div>
        </div>
      </article>
    `;
  }
}
